'use client';

// `useState` whose setter never schedules a re-render for a value that did
// not change.
//
// React already tries to do this: `dispatchSetState` eagerly evaluates the
// update and bails out when the result is `Object.is`-equal to the current
// state. But that fast path only applies while the fiber has NO pending
// work (`fiber.lanes === NoLanes`). The moment anything else has already
// scheduled an update on the same component — which, in this editor, is any
// render driven by an `EditorProvider` dispatch — the eager bailout is
// skipped and `setX(sameValue)` schedules a real render. A functional
// updater that returns `prev` unchanged is no better: React cannot know it
// is a no-op until it runs it, which happens during the extra render.
//
// That is normally invisible (one wasted render). It stops being invisible
// when the setter lives in a `useEffect` that re-runs on every editor state
// change, because then EVERY passive-effect flush schedules another update.
// React counts consecutive passive flushes that do that
// (`nestedPassiveUpdateCount`, limit 50) and logs "Maximum update depth
// exceeded. This can happen when a component calls setState inside
// useEffect..." — blaming whichever setState came next rather than the
// effect responsible. A continuous drag dispatches one scene edit per
// pointermove, so it reaches 50 in well under a second: that is the bug
// report that produced this file (dragging a vaulted ceiling node in the
// guided 2D ceiling editor, reported against CeilingProfileCanvas's own
// dispatch, actually caused by three unrelated effects elsewhere).
//
// Comparing before calling the setter is the only thing that stops the
// update being scheduled at all. Prefer this hook over hand-rolling that
// comparison for any state written from an effect.
//
// It is NOT a way to quiet `react-hooks/set-state-in-effect`. A genuine
// change still costs the render it always did — all this removes is the
// render for a value that did not change.
//
// Gate: web/tests/unit/effectUpdateBudget.test.tsx.

import { useCallback, useRef, useState } from 'react';

/** Not for state whose value is itself a function — the updater overload
 *  would be ambiguous, same as plain `useState`. */
export function useIdempotentState<T>(
  initial: T | (() => T),
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(initial);
  // Mirrors the value the caller has most recently ASKED for, which during a
  // burst of sets in one tick runs ahead of `value` — that is what makes a
  // second set in the same tick compare against the first rather than
  // against the last committed render.
  const pending = useRef(value);
  pending.current = value;

  const set = useCallback((next: T | ((prev: T) => T)) => {
    const resolved = typeof next === 'function'
      ? (next as (prev: T) => T)(pending.current)
      : next;
    if (Object.is(resolved, pending.current)) return;
    pending.current = resolved;
    setValue(resolved);
  }, []);

  return [value, set];
}
